// src/pages/DealerDetails.jsx
import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../utils/axios";
import toast from "react-hot-toast";
import {
  FaArrowLeft,
  FaUsers,
  FaBalanceScale,
  FaClipboardList,
  FaMapMarkerAlt,
  FaMobileAlt,
  FaIdCard,
  FaFilter,
  FaTimes,
  FaTrash,
  FaUpload,
  FaCalendarAlt,
  FaBars,
} from "react-icons/fa";
import { differenceInCalendarDays, isAfter, isBefore } from "date-fns";

// ✅ Your GAS endpoint that returns rows like:
// [{ TIMESTAMP:"04/02/2026 05:38:14", GST:"24AAECV3903E1ZP", PDF:"https://drive..." }, ...]
const GAS_PDF_URL =
  "https://script.google.com/macros/s/AKfycbzR9X4SN8qlLGVPYoZRx-_16siw8h9wr8x9CTT78N47GCR9OX8FoYk1SKq8Xe7Zc_qX/exec";

export default function DealerDetails() {
  const { dealerId } = useParams();
  const navigate = useNavigate();

  // Return to wherever the user came from — this page opens from more than
  // one list, so a hardcoded /admin-dashboard dropped them on the wrong page
  // (and lost their tab/scroll). Fall back only on a direct/deep link.
  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/admin-dashboard");
  };

  const [editingTarget, setEditingTarget] = useState(false);
  const [targetFrom, setTargetFrom] = useState("");
  const [targetTo, setTargetTo] = useState("");
  const [savingTarget, setSavingTarget] = useState(false);

  const [dealer, setDealer] = useState({
    totalPurchase: 0,
    users: [],
  });

  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [minSales, setMinSales] = useState("");
  const [showFilterModal, setShowFilterModal] = useState(false);

  // Rewards
  const [rewardsModalOpen, setRewardsModalOpen] = useState(false);
  const [dealerRewards, setDealerRewards] = useState([]);
  const [fromKg, setFromKg] = useState("");
  const [toKg, setToKg] = useState("");
  const [rewardsInput, setRewardsInput] = useState("");
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  // PDF
  const [pdfFile, setPdfFile] = useState(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [hasPdf, setHasPdf] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const rowsPerPage = 25;

  // History Modal
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedUserHistory, setSelectedUserHistory] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  // Mobile hamburger menu
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  /* ================= PDF HELPERS ================= */
  const normalizeGst = (v) =>
    String(v || "")
      .trim()
      .toUpperCase();

  const resolveDealerGst = (d) =>
    d?.dealerGst || d?.gst || d?.GST || d?.dealer?.gst || d?.dealer?.GST || "";

  const fetchPdfFromSheet = async (gst) => {
    const GST = normalizeGst(gst);
    if (!GST) return null;

    const res = await fetch(`${GAS_PDF_URL}?t=${Date.now()}`); // cache-bust
    const data = await res.json();
    if (!Array.isArray(data)) return null;

    const toDate = (ts) => {
      // "DD/MM/YYYY HH:mm:ss"
      const m = String(ts || "").match(
        /^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2}):(\d{2})$/,
      );
      if (!m) return new Date(0);
      const [, dd, mm, yyyy, HH, MI, SS] = m;
      return new Date(
        Number(yyyy),
        Number(mm) - 1,
        Number(dd),
        Number(HH),
        Number(MI),
        Number(SS),
      );
    };

    const matches = data
      .map((r) => ({
        gst: normalizeGst(r.GST),
        pdf: String(r.PDF || "").trim(),
        ts: String(r.TIMESTAMP || "").trim(),
      }))
      .filter((r) => r.gst === GST && r.pdf);

    if (!matches.length) return null;

    matches.sort((a, b) => toDate(b.ts) - toDate(a.ts));
    return matches[0]?.pdf || null;
  };

  /* ================= TARGET INFO ================= */
  const targetInfo = useMemo(() => {
    if (!dealer?.target?.startDate || !dealer?.target?.endDate) return null;

    const start = new Date(dealer.target.startDate);
    const end = new Date(dealer.target.endDate);
    const today = new Date();

    let remainingDays = 0;
    let status = "upcoming";

    if (isBefore(today, start)) {
      remainingDays = differenceInCalendarDays(end, start);
      status = "upcoming";
    } else if (isAfter(today, end)) {
      remainingDays = 0;
      status = "completed";
    } else {
      remainingDays = differenceInCalendarDays(end, today);
      status = "active";
    }

    let months = 0;
    let days = remainingDays;

    if (remainingDays >= 30) {
      months = Math.floor(remainingDays / 30);
      days = remainingDays % 30;
    }

    return { start, end, remainingDays, status, months, days };
  }, [dealer?.target]);

  const filtersActive = useMemo(() => {
    return Boolean(search.trim() || typeFilter || minSales);
  }, [search, typeFilter, minSales]);

  /* ================= FETCH DEALER ================= */
  useEffect(() => {
    const fetchDealer = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/admin/dealer/${dealerId}`);

        if (!res.data?.success) {
          toast.error("Dealer not found");
          navigate("/admin-dashboard");
          return;
        }

        const dealerData = res.data.dealer;

        // ✅ PDF: prefer backend url; if missing, pull latest from Sheet by GST
        const gst = resolveDealerGst(dealerData);
        let pdfUrl = dealerData?.pdf?.url || "";

        if (!pdfUrl && gst) {
          try {
            pdfUrl = await fetchPdfFromSheet(gst);
          } catch (e) {
            console.error("GAS pdf fetch failed:", e);
          }
        }

        // set dealer once with resolved pdf
        setDealer({
          ...dealerData,
          pdf: pdfUrl ? { url: pdfUrl } : dealerData?.pdf,
        });
        setHasPdf(!!pdfUrl);

        try {
          const rewardRes = await api.get(`/admin/dealers/${dealerId}/reward`);
          if (
            rewardRes.data?.success &&
            Array.isArray(rewardRes.data.rewards)
          ) {
            setDealerRewards(rewardRes.data.rewards);
            setRows(
              rewardRes.data.rewards.map((r) => ({
                range: `${r.fromKg} - ${r.toKg} KG`,
                rewards: r.rewards.join(" / "),
                fromKg: r.fromKg,
                toKg: r.toKg,
                new: false,
              })),
            );
          }
        } catch (rewardErr) {
          console.error("Failed to fetch rewards:", rewardErr);
        }
      } catch (err) {
        toast.error("Failed to load dealer");
        navigate("/admin-dashboard");
      } finally {
        setLoading(false);
      }
    };

    fetchDealer();
  }, [dealerId, navigate]);

  /* ================= PDF UPLOAD ================= */
  const uploadPdf = async () => {
    if (!pdfFile) return toast.error("Select PDF first");

    const dealerGst = resolveDealerGst(dealer);
    if (!dealerGst) return toast.error("Dealer GST not found");

    const formData = new FormData();
    formData.append("pdf", pdfFile);
    formData.append("dealerGst", dealerGst);
    formData.append("dealerId", dealerId);

    try {
      setUploadingPdf(true);

      const res = await api.post(`/admin/dealers/${dealerId}/upload-pdf`, formData);
      const data = res.data;

      toast.success("PDF uploaded!");

      // Prefer the URL returned by the backend; else fetch latest from GAS sheet
      let newUrl = data?.pdf?.url || data?.pdfUrl || data?.url || "";
      if (!newUrl) {
        try {
          newUrl = await fetchPdfFromSheet(dealerGst);
        } catch (e) {
          console.error("Fetch latest pdf after upload failed:", e);
        }
      }

      if (newUrl) {
        setDealer((prev) => ({ ...(prev || {}), pdf: { url: newUrl } }));
        setHasPdf(true);
      } else {
        setHasPdf(true);
      }

      setPdfFile(null);
    } catch (err) {
      console.error("PDF upload error:", err);
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.message ||
        (status ? `Upload failed (HTTP ${status})` : "Upload error");
      toast.error(msg);
    } finally {
      setUploadingPdf(false);
    }
  };

  /* ================= PROCESSED USERS ================= */
  const processedUsers = useMemo(() => {
    if (!dealer?.users) return [];

    const hasTarget = dealer.target?.startDate && dealer.target?.endDate;

    // No target window set → count ALL sales, the same way the dealer list
    // totals them. Zeroing them out here made a dealer showing 1,000 KG in
    // the list open as 0 KG.
    if (!hasTarget) {
      return dealer.users.map((u) => {
        const sales = Array.isArray(u.sales) ? u.sales : [];
        return {
          ...u,
          filteredSalesCount: sales.reduce(
            (sum, s) => sum + Number(s?.quantity || s?.salesCount || 0),
            0,
          ),
          filteredEntries: sales.length,
        };
      });
    }

    const start = new Date(dealer.target.startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(dealer.target.endDate);
    end.setHours(23, 59, 59, 999);

    return dealer.users.map((u) => {
      let filteredSalesCount = 0;
      let filteredEntries = 0;

      if (Array.isArray(u.sales) && u.sales.length > 0) {
        const validSales = u.sales.filter((s) => {
          if (!s?.date) return false;
          const sd = new Date(s.date);
          if (Number.isNaN(sd.getTime())) return false;
          return sd >= start && sd <= end;
        });

        filteredSalesCount = validSales.reduce(
          (sum, s) => sum + Number(s.quantity || s.salesCount || 0),
          0,
        );
        filteredEntries = validSales.length;
      }

      return {
        ...u,
        filteredSalesCount,
        filteredEntries,
      };
    });
  }, [dealer?.users, dealer?.target?.startDate, dealer?.target?.endDate]);

  /* ================= GST + TOTAL PURCHASE ================= */
  useEffect(() => {
    const fetchGstAndPurchase = async () => {
      if (!dealer?.gst) return;

      try {
        const res = await fetch(
          "https://script.google.com/macros/s/AKfycbxiM6gYXIcHWsg_hzejg_MxGPdXHhhlxlcDB6s-48KEqpMTVUkxd7Qx1T4q0-Z2y101zQ/exec",
        );
        const data = await res.json();

        const gst = String(dealer.gst || "").trim();

        const hasTargetWindow = Boolean(
          dealer?.target?.startDate && dealer?.target?.endDate,
        );

        const startDate = hasTargetWindow
          ? new Date(dealer.target.startDate)
          : null;
        const endDate = hasTargetWindow
          ? new Date(dealer.target.endDate)
          : null;

        if (hasTargetWindow) {
          startDate.setHours(0, 0, 0, 0);
          endDate.setHours(23, 59, 59, 999);
        }

        const gstMatches = (Array.isArray(data) ? data : []).filter(
          (row) => String(row?.GSTIN || "").trim() === gst,
        );

        const windowMatches = hasTargetWindow
          ? gstMatches.filter((row) => {
              const t = row?.TimeSatmp;
              if (!t) return false;
              const saleDate = new Date(t);
              if (Number.isNaN(saleDate.getTime())) return false;
              return saleDate >= startDate && saleDate <= endDate;
            })
          : gstMatches;

        const totalQty = windowMatches.reduce(
          (sum, item) => sum + Number(item?.["Current Dispt Qty"] || 0),
          0,
        );

        setDealer((prev) => ({
          ...prev,
          totalPurchase: totalQty,
          gstMatch: windowMatches.length > 0,
        }));
      } catch (err) {
        console.error("Failed to fetch GST data:", err);
        setDealer((prev) => ({ ...prev, totalPurchase: 0, gstMatch: false }));
      }
    };

    fetchGstAndPurchase();
  }, [dealer?.gst, dealer?.target?.startDate, dealer?.target?.endDate]);

  const handleUpdateTarget = async () => {
    if (!targetFrom || !targetTo) {
      toast.error("Please select both dates");
      return;
    }

    if (new Date(targetFrom) > new Date(targetTo)) {
      toast.error("From date cannot be after To date");
      return;
    }

    try {
      setSavingTarget(true);

      const res = await api.put(`/admin/dealer/${dealerId}/target`, {
        startDate: targetFrom,
        endDate: targetTo,
      });

      if (!res.data?.success)
        throw new Error(res.data?.message || "API failed");

      setDealer((prev) => ({
        ...prev,
        target: {
          startDate: targetFrom,
          endDate: targetTo,
        },
      }));

      toast.success("Target updated successfully");
      setEditingTarget(false);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to update target");
    } finally {
      setSavingTarget(false);
    }
  };

  /* ================= TOTALS ================= */
  const totals = useMemo(() => {
    let totalSales = 0;
    let totalEntries = 0;

    processedUsers.forEach((u) => {
      totalSales += u.filteredSalesCount || 0;
      totalEntries += u.filteredEntries || 0;
    });

    return { totalSales, totalEntries };
  }, [processedUsers]);

  /* ================= FILTER USERS ================= */
  const filteredUsers = useMemo(() => {
    const q = (search || "").trim().toLowerCase();

    return processedUsers.filter((u) => {
      const name = (u.name || "").toLowerCase();
      const mobile = (u.mobile || "").toLowerCase();
      const district = (u.district || u.location?.district || "").toLowerCase();
      const area = (u.area || "").toLowerCase();

      const matchSearch =
        !q ||
        name.includes(q) ||
        mobile.includes(q) ||
        district.includes(q) ||
        area.includes(q);

      const userType = (u.type || u.userType || u.category || "").toLowerCase();
      const matchType = typeFilter
        ? userType === typeFilter.toLowerCase()
        : true;

      const sales = u.filteredSalesCount;
      const matchSales = minSales ? sales >= Number(minSales) : true;

      return matchSearch && matchType && matchSales;
    });
  }, [processedUsers, search, typeFilter, minSales]);

  useEffect(() => {
    setPage(1);
  }, [search, typeFilter, minSales]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / rowsPerPage));
  const currentUsers = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredUsers.slice(start, start + rowsPerPage);
  }, [filteredUsers, page]);

  const goFirst = () => setPage(1);
  const goPrev = () => setPage((p) => Math.max(1, p - 1));
  const goNext = () => setPage((p) => Math.min(totalPages, p + 1));
  const goLast = () => setPage(totalPages);

  const resetFilters = () => {
    setSearch("");
    setTypeFilter("");
    setMinSales("");
    setPage(1);
  };

  const openPdf = () => {
    const pdfUrl = dealer?.pdf?.url;
    if (!pdfUrl) {
      toast.error("No PDF available");
      return;
    }
    window.open(pdfUrl, "_blank", "noopener,noreferrer");
  };

  const openHistoryModal = (user) => {
    if (!user) return;

    let history = user.sales || [];

    const hasTarget = dealer.target?.startDate && dealer.target?.endDate;
    if (hasTarget) {
      const start = new Date(dealer.target.startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(dealer.target.endDate);
      end.setHours(23, 59, 59, 999);

      history = history.filter((s) => {
        if (!s?.date) return false;
        const sd = new Date(s.date);
        if (Number.isNaN(sd.getTime())) return false;
        return sd >= start && sd <= end;
      });
    }

    const formattedHistory = history.map((s) => ({
      date: s.date,
      salesCount: Number(s.quantity || s.salesCount || 0),
      entryCount: 1,
    }));

    setSelectedUser(user);
    setSelectedUserHistory(formattedHistory);
    setHistoryModalOpen(true);
  };

  const formatRewards = (value) =>
    value
      .toUpperCase()
      .replace(/\s*\/\s*/g, " / ")
      .replace(/\s{2,}/g, " ")
      .trim();

  const addRow = () => {
    if (!fromKg || !toKg || !rewardsInput.trim()) {
      toast.error("Fill all fields");
      return;
    }
    if (Number(fromKg) >= Number(toKg)) {
      toast.error("From KG must be less than To KG");
      return;
    }

    setRows((prev) => [
      ...prev,
      {
        range: `${fromKg} - ${toKg} KG`,
        rewards: formatRewards(rewardsInput),
        fromKg: Number(fromKg),
        toKg: Number(toKg),
        new: true,
      },
    ]);

    setFromKg("");
    setToKg("");
    setRewardsInput("");
  };

  const removeRow = async (index) => {
    const row = rows[index];

    if (row.new) {
      setRows((prev) => prev.filter((_, i) => i !== index));
      toast.success("New row removed");
      return;
    }

    if (!window.confirm("Delete this reward range?")) return;

    try {
      const deleteRes = await api.delete(`/admin/dealers/${dealerId}/reward`, {
        data: { fromKg: row.fromKg, toKg: row.toKg },
      });

      if (deleteRes.data?.success) {
        toast.success("Reward range deleted");
        const refreshRes = await api.get(`/admin/dealers/${dealerId}/reward`);
        if (
          refreshRes.data?.success &&
          Array.isArray(refreshRes.data.rewards)
        ) {
          setDealerRewards(refreshRes.data.rewards);
          setRows(
            refreshRes.data.rewards.map((r) => ({
              range: `${r.fromKg} - ${r.toKg} KG`,
              rewards: r.rewards.join(" / "),
              fromKg: r.fromKg,
              toKg: r.toKg,
              new: false,
            })),
          );
        }
      }
    } catch (err) {
      toast.error("Failed to delete reward");
    }
  };

  const saveRewards = async () => {
    const newRows = rows.filter((r) => r.new);
    if (newRows.length === 0) {
      toast.error("No new rewards to save");
      return;
    }

    try {
      setSaving(true);

      const payload = newRows.map((r) => ({
        fromKg: r.fromKg,
        toKg: r.toKg,
        rewards: r.rewards.split("/").map((x) => x.trim()),
      }));

      const res = await api.post(`/admin/dealers/${dealerId}/reward`, {
        rewards: payload,
      });

      if (res.data?.success) {
        toast.success("Rewards saved successfully");
        const rewardRes = await api.get(`/admin/dealers/${dealerId}/reward`);
        if (rewardRes.data?.success && Array.isArray(rewardRes.data.rewards)) {
          setDealerRewards(rewardRes.data.rewards);
          setRows(
            rewardRes.data.rewards.map((r) => ({
              range: `${r.fromKg} - ${r.toKg} KG`,
              rewards: r.rewards.join(" / "),
              fromKg: r.fromKg,
              toKg: r.toKg,
              new: false,
            })),
          );
        }
      }
    } catch (err) {
      toast.error("Failed to save rewards");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-xl text-gray-600">Loading dealer details…</div>
      </div>
    );
  }

  if (!dealer) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ================= TOP BAR ================= */}
      <div className="bg-yellow-600 border-b shadow-sm sticky top-0 z-30">
        <div className="mx-auto px-4 sm:px-6 py-3 sm:py-4">
          {/* Row 1: Back + Name + Rewards + Hamburger */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={goBack}
                className="text-white text-xl sm:text-2xl"
              >
                <FaArrowLeft />
              </button>
              <h1 className="text-xl sm:text-3xl font-extrabold text-white truncate max-w-[220px] sm:max-w-none">
                {dealer.name}
              </h1>
            </div>

            <div className="flex items-center gap-4">
              <button
                onClick={() => setRewardsModalOpen(true)}
                className="flex items-center gap-1.5 text-white text-sm sm:text-base font-medium md:hidden"
              >
                <span className="text-xl sm:text-2xl">🎁</span>
                <span className="hidden sm:inline">Rewards</span>
                {dealerRewards.length > 0 && (
                  <span className="bg-white text-yellow-700 text-xs px-2 py-0.5 rounded-full font-bold">
                    {dealerRewards.length}
                  </span>
                )}
              </button>

              <button
                className="sm:hidden text-white text-2xl"
                onClick={() => setShowMobileMenu(!showMobileMenu)}
              >
                <FaBars />
              </button>
            </div>
          </div>

          {/* Target Period + Remaining Days */}
          {targetInfo && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-blue-700/20 border border-blue-400 rounded-xl p-3.5">
                <div className="flex items-center gap-2.5 mb-1.5">
                  <FaCalendarAlt className="text-blue-300 text-xl" />
                  <p className="text-sm font-semibold text-white">
                    Target Period
                  </p>
                </div>

                {!editingTarget ? (
                  <div className="flex flex-wrap items-center gap-2 text-white text-sm font-medium">
                    <span>{targetInfo.start.toLocaleDateString("en-IN")}</span>
                    <span className="opacity-80">→</span>
                    <span>{targetInfo.end.toLocaleDateString("en-IN")}</span>
                    <button
                      onClick={() => {
                        setEditingTarget(true);
                        setTargetFrom(
                          new Date(dealer.target.startDate)
                            .toISOString()
                            .slice(0, 10),
                        );
                        setTargetTo(
                          new Date(dealer.target.endDate)
                            .toISOString()
                            .slice(0, 10),
                        );
                      }}
                      className="ml-2 text-blue-200 hover:text-blue-100 text-lg"
                      title="Edit Target"
                    >
                      ✏️
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2.5 mt-1">
                    <input
                      type="date"
                      value={targetFrom}
                      onChange={(e) => setTargetFrom(e.target.value)}
                      className="w-full text-sm bg-white/90 p-2 rounded text-black border border-gray-300"
                    />
                    <input
                      type="date"
                      value={targetTo}
                      onChange={(e) => setTargetTo(e.target.value)}
                      className="w-full text-sm bg-white/90 p-2 rounded text-black border border-gray-300"
                    />
                    <div className="flex gap-2.5">
                      <button
                        onClick={handleUpdateTarget}
                        disabled={savingTarget}
                        className="flex-1 bg-green-600 text-white py-2 rounded text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                      >
                        {savingTarget ? "Saving..." : "Save"}
                      </button>
                      <button
                        onClick={() => setEditingTarget(false)}
                        className="flex-1 bg-gray-600 text-white py-2 rounded text-sm font-medium hover:bg-gray-700"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div
                className={`rounded-xl p-3.5 text-center border flex flex-col justify-center ${
                  targetInfo.status === "completed"
                    ? "bg-red-700/20 border-red-400"
                    : targetInfo.status === "upcoming"
                      ? "bg-gray-700/20 border-gray-400"
                      : "bg-green-700/20 border-green-400"
                }`}
              >
                <p className="text-white font-bold text-xl sm:text-2xl">
                  {targetInfo.status === "completed"
                    ? "Completed"
                    : targetInfo.months > 0
                      ? `${targetInfo.months}M ${targetInfo.days}D`
                      : `${targetInfo.days}D`}
                </p>
                <p className="text-white/90 text-sm mt-1">Remaining Days</p>
              </div>
            </div>
          )}

          {/* Mobile Hamburger Menu */}
          {showMobileMenu && (
            <div className="mt-4 bg-yellow-700 rounded-xl p-5 shadow-xl sm:hidden">
              <div className="flex flex-col gap-4 text-white">
                {!!dealer?.pdf?.url && (
                  <button
                    onClick={() => {
                      openPdf();
                      setShowMobileMenu(false);
                    }}
                    className="flex items-center gap-3 px-4 py-3 bg-green-600/80 rounded-lg hover:bg-green-700"
                  >
                    <span className="text-xl">📄</span> View PDF
                  </button>
                )}

                {(dealer.district || dealer.taluka || dealer.area) && (
                  <div className="flex items-center gap-3 px-4 py-3 bg-yellow-800/50 rounded-lg">
                    <FaMapMarkerAlt className="text-lg" />
                    <span className="truncate">
                      {dealer.district}
                      {dealer.taluka ? `, ${dealer.taluka}` : ""}
                      {dealer.area ? ` - ${dealer.area}` : ""}
                    </span>
                  </div>
                )}

                {dealer.mobile && (
                  <div className="flex items-center gap-3 px-4 py-3 bg-yellow-800/50 rounded-lg">
                    <FaMobileAlt className="text-lg" />
                    <span>{dealer.mobile}</span>
                  </div>
                )}

                {dealer.gst && (
                  <div className="flex items-center gap-3 px-4 py-3 bg-yellow-800/50 rounded-lg">
                    <FaIdCard className="text-lg" />
                    <span className="truncate">{dealer.gst}</span>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div className="bg-yellow-800/50 p-3 rounded-lg text-center">
                    <FaUsers className="mx-auto text-2xl mb-1 text-yellow-300" />
                    <div className="text-sm font-bold">
                      {dealer.users?.length || 0}
                    </div>
                    <div className="text-xs opacity-80">Customers</div>
                  </div>
                  <div className="bg-yellow-800/50 p-3 rounded-lg text-center">
                    <FaBalanceScale className="mx-auto text-2xl mb-1 text-yellow-300" />
                    <div className="text-sm font-bold">
                      {Math.floor(dealer.totalPurchase || 0).toLocaleString()}{" "}
                      KG
                    </div>
                    <div className="text-xs opacity-80">Purchase</div>
                  </div>
                  <div className="bg-yellow-800/50 p-3 rounded-lg text-center">
                    <FaBalanceScale className="mx-auto text-2xl mb-1 text-yellow-300" />
                    <div className="text-sm font-bold">
                      {totals.totalSales.toLocaleString()} KG
                    </div>
                    <div className="text-xs opacity-80">Sales</div>
                  </div>
                  <div className="bg-yellow-800/50 p-3 rounded-lg text-center">
                    <FaClipboardList className="mx-auto text-2xl mb-1 text-yellow-300" />
                    <div className="text-sm font-bold">
                      {totals.totalEntries}
                    </div>
                    <div className="text-xs opacity-80">Entries</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Desktop Extra Info */}
          <div className="hidden md:flex flex-col lg:flex-row lg:justify-between gap-5 mt-4">
            <div className="min-w-0 flex flex-col gap-3">
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {!!dealer?.pdf?.url && (
                  <button
                    onClick={openPdf}
                    className="flex items-center gap-2 text-sm px-4 py-1.5 rounded-full bg-green-100 text-green-800 hover:bg-green-200"
                  >
                    📄 View PDF
                  </button>
                )}

                {(dealer.district || dealer.taluka || dealer.area) && (
                  <p className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-yellow-100/80 text-sm text-black/90">
                    <FaMapMarkerAlt />
                    <span className="truncate">
                      {dealer.district || ""}
                      {dealer.taluka ? `, ${dealer.taluka}` : ""}
                      {dealer.area ? ` - ${dealer.area}` : ""}
                    </span>
                  </p>
                )}

                {dealer.mobile && (
                  <p className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-yellow-100/80 text-sm text-black/90">
                    <FaMobileAlt />
                    <span className="truncate">{dealer.mobile}</span>
                  </p>
                )}

                {dealer.gst && (
                  <p className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-yellow-100/80 text-sm text-black/90">
                    <FaIdCard />
                    <span className="truncate">{dealer.gst}</span>
                  </p>
                )}
              </div>

              <button
                onClick={() => setRewardsModalOpen(true)}
                className="flex items-center w-fit gap-2 text-sm px-4 py-1.5 rounded-full bg-purple-100 text-purple-800 hover:bg-purple-200 transition font-medium"
              >
                🎁 Rewards
                {dealerRewards.length > 0 && (
                  <span className="bg-purple-600 text-white text-xs px-2 py-0.5 rounded-full">
                    {dealerRewards.length}
                  </span>
                )}
              </button>
            </div>

            <div className="flex flex-wrap gap-3 justify-end">
              <div className="flex items-center gap-3 bg-yellow-50 border border-yellow-200 px-5 py-3 rounded-xl">
                <FaUsers className="text-yellow-600 text-xl" />
                <div>
                  <p className="text-xs text-gray-500 font-semibold">
                    Customers
                  </p>
                  <p className="font-extrabold text-gray-900">
                    {dealer.users?.length || 0}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-yellow-50 border border-yellow-200 px-5 py-3 rounded-xl">
                <FaBalanceScale className="text-yellow-600 text-xl" />
                <div>
                  <p className="text-xs text-gray-500 font-semibold">
                    Total Purchase
                  </p>
                  <p className="font-extrabold text-gray-900">
                    {Math.floor(dealer.totalPurchase || 0).toLocaleString()} KG
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-yellow-50 border border-yellow-200 px-5 py-3 rounded-xl">
                <FaBalanceScale className="text-yellow-600 text-xl" />
                <div>
                  <p className="text-xs text-gray-500 font-semibold">
                    Total Sales
                  </p>
                  <p className="font-extrabold text-gray-900">
                    {totals.totalSales.toLocaleString()} KG
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-yellow-50 border border-yellow-200 px-5 py-3 rounded-xl">
                <FaClipboardList className="text-yellow-600 text-xl" />
                <div>
                  <p className="text-xs text-gray-500 font-semibold">Entries</p>
                  <p className="font-extrabold text-gray-900">
                    {totals.totalEntries}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ================= MAIN CONTENT ================= */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Filters */}
        <div className="bg-white rounded-2xl border shadow-sm p-5 mb-6">
          {/* Mobile: Filter Button + Modal Trigger */}
          <div className="md:hidden flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">
              Customers List
            </h3>

            <button
              onClick={() => setShowFilterModal(true)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl border font-medium transition ${
                filtersActive
                  ? "bg-red-50 text-red-600 border-red-300 hover:bg-red-100"
                  : "bg-yellow-50 text-yellow-700 border-yellow-300 hover:bg-yellow-100"
              }`}
            >
              <FaFilter className="text-lg" />
              Filter
              {filtersActive && (
                <span className="bg-red-600 text-white text-xs px-2 py-0.5 rounded-full">
                  Active
                </span>
              )}
            </button>
          </div>

          {/* Desktop: Always visible filters */}
          <div className="hidden md:block">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-center gap-3">
                <button
                  onClick={resetFilters}
                  className={`h-11 w-11 rounded-xl grid place-items-center border transition ${
                    filtersActive
                      ? "bg-red-50 text-red-600 hover:bg-red-100 border-red-200"
                      : "bg-yellow-50 text-yellow-700 hover:bg-yellow-100 border-yellow-200"
                  }`}
                >
                  {filtersActive ? "×" : <FaFilter />}
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 flex-1">
                <input
                  type="text"
                  placeholder="Search name, mobile, area…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-11 px-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />

                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="h-11 px-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-500"
                >
                  <option value="">All Types</option>
                  <option>Kadiya</option>
                  <option>Mason</option>
                  <option>Contractor</option>
                  <option>Architect</option>
                  <option>Builder</option>
                  <option>Civil Engineer / Consultant</option>
                  <option>Fabricator</option>
                  <option>End User (Individual House)</option>
                </select>

                <input
                  type="number"
                  placeholder="Min Sales (KG)"
                  value={minSales}
                  onChange={(e) => setMinSales(e.target.value)}
                  className="h-11 px-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>
            </div>
          </div>

          {/* Mobile Filter Modal / Bottom Sheet */}
          {showFilterModal && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:hidden">
              <div className="bg-white w-full rounded-t-3xl max-h-[85vh] overflow-y-auto shadow-2xl">
                {/* Header */}
                <div className="sticky top-0 bg-white z-10 border-b px-5 py-4 flex items-center justify-between">
                  <h3 className="text-xl font-bold text-gray-900">Filters</h3>
                  <button
                    onClick={() => setShowFilterModal(false)}
                    className="text-gray-500 hover:text-gray-800 text-2xl"
                  >
                    <FaTimes />
                  </button>
                </div>

                {/* Filter Content */}
                <div className="p-5 space-y-6">
                  {/* Search */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Search
                    </label>
                    <input
                      type="text"
                      placeholder="Name, mobile, area..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="w-full h-12 px-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    />
                  </div>

                  {/* Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Customer Type
                    </label>
                    <select
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                      className="w-full h-12 px-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    >
                      <option value="">All Types</option>
                      <option>Kadiya</option>
                      <option>Mason</option>
                      <option>Contractor</option>
                      <option>Architect</option>
                      <option>Builder</option>
                      <option>Civil Engineer / Consultant</option>
                      <option>Fabricator</option>
                      <option>End User (Individual House)</option>
                    </select>
                  </div>

                  {/* Min Sales */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Minimum Sales (KG)
                    </label>
                    <input
                      type="number"
                      placeholder="e.g. 100"
                      value={minSales}
                      onChange={(e) => setMinSales(e.target.value)}
                      className="w-full h-12 px-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-4 pt-4 border-t">
                    <button
                      onClick={() => {
                        resetFilters();
                        setShowFilterModal(false);
                      }}
                      className="flex-1 py-3 bg-gray-200 text-gray-800 rounded-xl font-medium hover:bg-gray-300 transition"
                    >
                      Clear All
                    </button>
                    <button
                      onClick={() => setShowFilterModal(false)}
                      className="flex-1 py-3 bg-yellow-600 text-white rounded-xl font-medium hover:bg-yellow-700 transition"
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Table / Cards container */}
        <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
          {/* Header with showing info + pagination */}
          <div className="px-4 py-3 border-b flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 flex-wrap">
            <div className="text-sm text-gray-600">
              Showing{" "}
              <b>
                {filteredUsers.length === 0 ? 0 : (page - 1) * rowsPerPage + 1}
              </b>{" "}
              - <b>{Math.min(page * rowsPerPage, filteredUsers.length)}</b> of{" "}
              <b>{filteredUsers.length}</b>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={goFirst}
                  disabled={page === 1}
                  className="h-10 px-4 rounded-xl bg-gray-100 border hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition font-semibold"
                >
                  First
                </button>
                <button
                  onClick={goPrev}
                  disabled={page === 1}
                  className="h-10 px-4 rounded-xl bg-gray-100 border hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition font-semibold"
                >
                  Prev
                </button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages })
                    .slice(
                      Math.max(page - 3, 0),
                      Math.min(page + 2, totalPages),
                    )
                    .map((_, idx) => {
                      const p = Math.max(page - 3, 0) + idx + 1;
                      return (
                        <button
                          key={p}
                          onClick={() => setPage(p)}
                          className={`h-10 w-10 rounded-xl border transition font-semibold ${
                            p === page
                              ? "bg-yellow-600 text-white border-yellow-600"
                              : "bg-white hover:bg-gray-100"
                          }`}
                        >
                          {p}
                        </button>
                      );
                    })}
                </div>

                <button
                  onClick={goNext}
                  disabled={page === totalPages}
                  className="h-10 px-4 rounded-xl bg-gray-100 border hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition font-semibold"
                >
                  Next
                </button>
                <button
                  onClick={goLast}
                  disabled={page === totalPages}
                  className="h-10 px-4 rounded-xl bg-gray-100 border hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition font-semibold"
                >
                  Last
                </button>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="divide-y md:divide-y-0">
            {currentUsers.length === 0 ? (
              <div className="py-12 text-center text-gray-500">
                No Customers found
              </div>
            ) : (
              <>
                {/* Desktop Table – visible from md (768px) */}
                <div className="hidden md:block overflow-auto">
                  <table className="w-full min-w-[980px] border-collapse">
                    <thead className="bg-gray-100 sticky top-0 z-20 border-b">
                      <tr>
                        <th className="px-6 py-4 text-left">Name</th>
                        <th className="px-6 py-4 text-left">Mobile</th>
                        <th className="px-6 py-4 text-center">Sales (KG)</th>
                        <th className="px-6 py-4 text-center">Entries</th>
                        <th className="px-6 py-4 text-center">Type</th>
                        <th className="px-6 py-4 text-center">Location</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {currentUsers.map((u) => (
                        <tr
                          key={u._id}
                          className="hover:bg-yellow-50 cursor-pointer"
                          onClick={() => openHistoryModal(u)}
                        >
                          <td className="px-6 py-4 font-medium">
                            {u.name || "—"}
                          </td>
                          <td className="px-6 py-4">{u.mobile || "—"}</td>
                          <td className="px-6 py-4 text-center font-bold text-yellow-700">
                            {u.filteredSalesCount.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 text-center">
                            {u.filteredEntries}
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold uppercase bg-yellow-100 text-yellow-800 border border-yellow-200">
                              {u.type || u.userType || "—"}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            {u.district || u?.location?.district || "—"}
                            {u.area && (
                              <span className="text-gray-500">, {u.area}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden divide-y divide-gray-100">
                  {currentUsers.map((u) => (
                    <div
                      key={u._id || u.id}
                      onClick={() => openHistoryModal(u)}
                      className="p-4 hover:bg-yellow-50/60 active:bg-yellow-100/70 transition-colors cursor-pointer"
                    >
                      <div className="flex justify-between items-baseline mb-3">
                        <h3 className="font-bold text-lg text-gray-900 truncate pr-2 max-w-[62%]">
                          {u.name || "—"}
                        </h3>
                        <div className="text-right shrink-0">
                          <div className="text-2xl font-extrabold text-yellow-600">
                            {u.filteredSalesCount.toLocaleString()}
                            <span className="text-xl font-bold"> kg</span>
                          </div>
                          <div className="text-xs text-purple-700 font-medium mt-0.5">
                            {u.filteredEntries}{" "}
                            {u.filteredEntries === 1 ? "entry" : "entries"}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-2.5 text-sm">
                        <div className="text-gray-500 font-medium">Mobile</div>
                        <div className="font-medium text-gray-800">
                          {u.mobile || "—"}
                        </div>

                        <div className="text-gray-500 font-medium">Type</div>
                        <div>
                          <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wide bg-yellow-100 text-yellow-800 border border-yellow-200">
                            {u.type || u.userType || "—"}
                          </span>
                        </div>

                        <div className="text-gray-500 font-medium">
                          Location
                        </div>
                        <div className="text-gray-800">
                          {u.district || u?.location?.district || "—"}
                          {u.area ? (
                            <span className="text-gray-600">, {u.area}</span>
                          ) : null}
                        </div>

                        <div className="text-gray-500 font-medium">
                          Added On
                        </div>
                        <div className="text-gray-800">
                          {u.createdAt
                            ? new Date(u.createdAt).toLocaleDateString(
                                "en-IN",
                                {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                },
                              )
                            : "—"}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Bottom pagination for mobile */}
          {totalPages > 1 && (
            <div className="border-t px-4 py-3 flex justify-center md:hidden">
              <div className="flex items-center gap-4">
                <button
                  disabled={page === 1}
                  onClick={goPrev}
                  className="h-10 px-5 rounded-xl bg-gray-100 border hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition font-semibold"
                >
                  ← Prev
                </button>
                <span className="font-medium">Page {page}</span>
                <button
                  disabled={page === totalPages}
                  onClick={goNext}
                  className="h-10 px-5 rounded-xl bg-gray-100 border hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition font-semibold"
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ================= HISTORY MODAL ================= */}
      {historyModalOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-lg max-w-md w-full p-6 flex flex-col max-h-[80vh] relative">
            <button
              onClick={() => setHistoryModalOpen(false)}
              className="absolute top-3 right-3 text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-full p-2 transition"
            >
              <FaTimes size={16} />
            </button>

            <h2 className="text-xl font-extrabold mb-4">
              {selectedUser?.name} Sales Info
            </h2>

            <div className="flex-1 overflow-y-auto mb-4">
              {selectedUserHistory.length === 0 ? (
                <p className="text-gray-500 text-center py-10">No sales data</p>
              ) : (
                <div className="flex flex-col gap-3">
                  {selectedUserHistory.map((h, idx) => (
                    <div
                      key={idx}
                      className="bg-yellow-50 border border-yellow-200 rounded-xl p-4"
                    >
                      <p className="text-sm text-gray-500">
                        {h.date
                          ? new Date(h.date).toLocaleDateString("en-IN")
                          : "—"}
                      </p>
                      <p className="font-extrabold text-yellow-700">
                        Sales: {Number(h.salesCount || 0).toLocaleString()} KG
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-yellow-100 border-t border-yellow-300 rounded-xl p-4 sticky bottom-0">
              <p className="font-extrabold text-gray-800">
                Total Sales:{" "}
                {selectedUserHistory
                  .reduce((a, b) => a + Number(b.salesCount || 0), 0)
                  .toLocaleString()}{" "}
                KG
              </p>
              <p className="font-semibold text-gray-700">
                Total Entries: {selectedUserHistory.length}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ================= REWARDS MODAL ================= */}
      {rewardsModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col border border-gray-200">
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-2 rounded-full">
                  <span className="text-2xl">🎁</span>
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Dealer Rewards</h2>
                  <p className="text-sm text-purple-100 opacity-90">
                    {dealer?.name}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setRewardsModalOpen(false)}
                className="text-white hover:text-purple-100 text-3xl"
              >
                ×
              </button>
            </div>

            <div className="p-6 flex-1 overflow-y-auto bg-gray-50">
              <div className="mb-8 p-6 bg-white border border-purple-200 rounded-2xl shadow-sm">
                <h3 className="text-lg font-bold text-purple-800 mb-4">
                  Add New Reward Range
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <input
                    type="number"
                    placeholder="From KG"
                    value={fromKg}
                    onChange={(e) => setFromKg(e.target.value)}
                    className="border rounded-xl p-3 focus:ring-purple-500"
                  />
                  <input
                    type="number"
                    placeholder="To KG"
                    value={toKg}
                    onChange={(e) => setToKg(e.target.value)}
                    className="border rounded-xl p-3 focus:ring-purple-500"
                  />
                  <div className="md:col-span-3">
                    <input
                      type="text"
                      placeholder="Rewards (COOKER / BLENDER / FAN)"
                      value={rewardsInput}
                      onChange={(e) =>
                        setRewardsInput(e.target.value.toUpperCase())
                      }
                      className="border rounded-xl p-3 w-full focus:ring-purple-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Separate with / or comma
                    </p>
                  </div>
                </div>
                <div className="mt-5 flex justify-end">
                  <button
                    onClick={addRow}
                    className="px-6 py-2 bg-yellow-600 text-white rounded-xl font-bold hover:bg-yellow-700"
                  >
                    Add Row
                  </button>
                </div>
              </div>

              {rows.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full border rounded-xl">
                    <thead className="bg-yellow-100">
                      <tr>
                        <th className="p-4 text-left font-semibold">
                          Target (KG)
                        </th>
                        <th className="p-4 text-left font-semibold">Rewards</th>
                        <th className="p-4 text-center font-semibold">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, index) => (
                        <tr key={index} className="border-t hover:bg-gray-50">
                          <td className="p-4 font-medium">{row.range}</td>
                          <td className="p-4">{row.rewards}</td>
                          <td className="p-4 text-center">
                            <button
                              onClick={() => removeRow(index)}
                              className="text-red-600 hover:text-red-800 p-2 rounded-full"
                            >
                              <FaTrash className="text-lg" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div className="mt-8 p-6 bg-white border border-blue-200 rounded-2xl shadow-sm">
                    <h3 className="text-lg font-bold text-blue-800 mb-4 flex items-center gap-2">
                      <span className="text-xl">📄</span> Dealer PDF Document
                    </h3>

                    <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
                      <div className="flex-1 flex items-center gap-3">
                        <input
                          type="file"
                          accept="application/pdf"
                          onChange={(e) =>
                            setPdfFile(e.target.files?.[0] || null)
                          }
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                        {pdfFile && (
                          <button
                            onClick={uploadPdf}
                            disabled={uploadingPdf}
                            className={`px-5 py-2 rounded-xl text-white font-medium flex items-center gap-2 transition ${
                              uploadingPdf
                                ? "bg-gray-400 cursor-not-allowed"
                                : "bg-blue-600 hover:bg-blue-700"
                            }`}
                          >
                            <FaUpload className="text-base" />
                            {uploadingPdf ? "Uploading..." : "Upload"}
                          </button>
                        )}
                      </div>

                      {!!dealer?.pdf?.url && (
                        <button
                          onClick={openPdf}
                          className="flex items-center gap-2 text-sm px-4 py-1 rounded-full bg-green-100 text-green-800 hover:bg-green-200"
                        >
                          📄 View PDF
                        </button>
                      )}
                    </div>

                    <p className="text-xs text-gray-500 mt-3">
                      Upload latest reward/incentive PDF for this dealer. Max
                      5MB recommended.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  No rewards added yet. Use the form above to create new reward
                  ranges.
                </div>
              )}
            </div>

            <div className="bg-white border-t p-5 flex items-center justify-between">
              <button
                onClick={() => setRewardsModalOpen(false)}
                className="px-6 py-3 bg-gray-200 text-gray-800 rounded-xl hover:bg-gray-300"
              >
                Close
              </button>

              <button
                onClick={saveRewards}
                disabled={saving || !rows.some((r) => r.new)}
                className={`px-6 py-3 rounded-xl font-semibold text-white transition ${
                  saving || !rows.some((r) => r.new)
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-green-600 hover:bg-green-700"
                }`}
              >
                {saving ? "Saving..." : "Save All Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
